import { Component, DoCheck, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NB_SEANCE_MAX_TARIFICATION_AUTO } from 'apps/idl/src/anakin/shared/seances.constants';
import { DateHelper, NumberHelper } from 'apps/idl/src/helpers';
import { IContact } from 'apps/idl/src/model';
import { EStatusSeance } from 'apps/idl/src/model/EStatusSeance';
import { Invoice } from 'apps/idl/src/modules/facturation/models/invoice';
import { AMCP } from 'apps/idl/src/modules/patients/model/amc-p';
import { IAMC } from 'apps/idl/src/modules/patients/model/iamc';
import { IAMO } from 'apps/idl/src/modules/patients/model/iamo';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ISeance } from '../../../../../model/ISeance';
import { Traitement } from '../../../../../model/Traitement';
import { IPatient } from '../../../../../modules/patients/model/IPatient';
import { StoredSeance } from '../../../../models/StoredSeance';
import { EBoutonIconFiltreSeancesListMode } from '../../enums/EBoutonIconFiltreSeancesListMode';
import { ESeancesListMode } from '../../enums/ESeancesListMode';
import { DeviceService } from '../../services/device.service';
import { LoaderService } from '../../services/loader.service';
import { PanneauService } from '../../services/panneau.service';
import { SeanceRealisationService } from '../../services/seance-realisation.service';
import { SeanceService } from '../../services/seance.service';

@Component({
  selector: 'di-groupe-liste-seance',
  templateUrl: './groupe-liste-seances-component.html',
  styleUrls: ['./groupe-liste-seances-component.scss']
})
export class GroupeListeSeancesComponent extends DestroyableComponentBase implements OnInit, OnChanges, DoCheck {

  @Input() ordonnance: Traitement;
  @Input() patient: IPatient;
  @Input() seances?: StoredSeance[];
  @Input() facturationStart: boolean = false;
  @Input() isFacturable$: BehaviorSubject<boolean>;
  @Input() amoCouverture: IAMO;
  @Input() amcCouverture: IAMC;
  @Input() amcPatient: AMCP;
  @Input() infirmiers: IContact[];
  @Input() targetSeanceId?: string;

  @Input() nomPatientFormatted?: string;
  @Input() adressePatientFormatted?: string;
  @Input() libelleBtnDeplacement?: string;
  @Input() openPanneauDeplacement?: Function;
  @Input() filtreForm?: FormGroup;
  @Input() onDateFacturationChange?: Function;
  @Input() filterInfirmiersSelected$: BehaviorSubject<string[]>;
  @Input() openMenuFiltreInfirmier?: Function;
  @Input() openMenuFiltreEtat?: Function;
  @Input() colorFilter$: BehaviorSubject<string>;
  @Input() factureTarifiee: Invoice;
  @Input() showWarningTarification: boolean = false;
  @Input() filterEtatsSelected: string[];
  @Input() preparer?: Function;
  @Input() facturer?: Function;
  @Input() showLoaderTarification: boolean = false;
  @Input() messageErreurTarification: string = "";

  public groupedSeances: { date: string, seances: ISeance[] }[] = [];
  public isMobile: boolean = false;
  public libelleBtnToutRealiser: string = "Tout réaliser";
  public isToutRealiser: boolean = false;
  public maxDateFacturation: Date = new Date();

  /**Gestion tarification à la volée */
  public libelleBtnFacturer: string = "Facturer";
  public warningTarificationMessage: string = `Pour des raisons de performances la tarification automatique est limitée à ${NB_SEANCE_MAX_TARIFICATION_AUTO} séances.`;

  private previousSeanceIds = [];
  public isFacturable: boolean;
  public colorFilter: string;
  public filterInfirmiersSelected: string[];

  public seancesListMode: ESeancesListMode = ESeancesListMode.DETAILED;
  public seancesListOptions = Object.values(ESeancesListMode);
  public boutonIconSeancesListOptions = EBoutonIconFiltreSeancesListMode;

  public messageSSIADHAD: string = "Facturation dans le cadre d'un HAD ou SSIAD , la facture est générée au format papier.";
  public seanceErreurs: Record<string, string> = {};

  constructor(
    private svcPanneau: PanneauService,
    private router: Router,
    private svcDevice: DeviceService,
    private svcSeance: SeanceService,
    private svcSeanceRealisation: SeanceRealisationService,
    private svcLoader: LoaderService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.groupSeancesByDate();
    this.verifierEtatBtnRealiser();

    merge(
      this.svcDevice.isMobile$.pipe(tap(flag => this.isMobile = flag)),
      this.isFacturable$.pipe(tap(flag => this.isFacturable = flag)),
      this.colorFilter$.pipe(tap(color => this.colorFilter = color)),
      this.filterInfirmiersSelected$.pipe(tap(infirmiers => this.filterInfirmiersSelected = infirmiers)),
      this.svcSeance.seanceUpdated$.pipe(
        tap((seance: StoredSeance) => {
          delete this.seanceErreurs[seance._id];
        })
      )
    ).pipe(takeUntil(this.destroyed$))
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['seances']) {
      this.groupSeancesByDate();
    }
    if (changes['factureTarifiee']) {
      this.setLibelleBtnFacturer();
    }
    this.verifierEtatBtnRealiser();
  }

  private seancesHasChanged(): boolean {
    const newSignatures = this.seances.map(s => `${s._id}__${s._rev}`);

    const changed =
      newSignatures.length !== this.previousSeanceIds.length ||
      newSignatures.some((sig, i) => sig !== this.previousSeanceIds[i]);

    this.previousSeanceIds = [...newSignatures];

    return changed;
  }


  // Quand on ouvre le composant dans un panneau sur mobile, 
  // ngChange n'est pas déclenché lorsqu'on modifie le contenu de this.seances
  // On utilise iterableDiffers pour détecter un changement au niveau du contenu du this.seances.
  ngDoCheck(): void {
    if (this.seancesHasChanged()) {
      this.groupSeancesByDate();
      this.verifierEtatBtnRealiser();
    }
  }

  private setLibelleBtnFacturer() {
    if (this.factureTarifiee) {
      const nbSeancesAFacturer: number = this.seances.filter(x => x.status === EStatusSeance.done).length || 0;
      const totalAFacturer: number = this.factureTarifiee.totalPartAMC + this.factureTarifiee.totalPartAMO + this.factureTarifiee.totalPartPatient;
      const totalAFacturerFormate: string = NumberHelper.round(totalAFacturer, 2);
      this.libelleBtnFacturer = `Facturer  ${nbSeancesAFacturer} séance${nbSeancesAFacturer > 1 ? 's' : ''} (${totalAFacturerFormate} €)`;
    } else {
      this.libelleBtnFacturer = "Facturer";
    }
  }

  private setAfficherLoaderTarif() {
    //On affiche le loader de tarification si on a des séances à facturer
    const nbSeancesDone = this.seances.filter(x => x.status === EStatusSeance.done).length;
    this.showLoaderTarification = nbSeancesDone > 0 && nbSeancesDone <= NB_SEANCE_MAX_TARIFICATION_AUTO;
  }

  onClickFacturer(event?: Event): void {
    if(this.facturer) this.facturer(event)
  }

  onClickPreparer(event?: Event): void {
    if(this.preparer) this.preparer(event);
  }

  onClickCancel() {
    if (this.isMobile) {
      this.svcPanneau.close();
    } else {
      this.router.navigate(["facturation"]);
    }
  }


  private groupSeancesByDate(): void {
    const seancesByDate: { [key: string]: ISeance[] } = {};
    const today = new Date();


    this.seances.forEach(seance => {
      const dateKey = this.getFormattedDate(new Date(seance.startDate), today);
      if (!seancesByDate[dateKey]) {
        seancesByDate[dateKey] = [];
      }
      seancesByDate[dateKey].push(seance);
    });

    this.groupedSeances = [];
    this.groupedSeances = Object.keys(seancesByDate).map(date => ({
      date: date,
      seances: seancesByDate[date]
    }));
  }

  public getFormattedDate(date: Date, today: Date): string {

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const isToday = DateHelper.compareTwoDates(dateOnly, todayOnly) === 0;

    return isToday
      ? `Aujourd'hui, ${date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
      : date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }

  public handleClickRealiser = (seancesToUpdate?: StoredSeance[]) => {
    if(!seancesToUpdate){
      seancesToUpdate = this.seances.filter((seance: StoredSeance) => seance.status !== EStatusSeance.canceled && seance.status !== EStatusSeance.paused);
    }

    const seancesUpdated$: Observable<StoredSeance[]> = this.isToutRealiser
      ? this.svcSeanceRealisation.realiserSeancesSilent(seancesToUpdate, this.patient,this.ordonnance).pipe(
          tap((mapResult) => {
            Array.from(mapResult.values()).forEach(result => {
              if(result.isDone) {
                delete this.seanceErreurs[result.seance._id];
              }else{
                this.seanceErreurs[result.seance._id] = result.erreur;
              }
            });
          }),
          map((mapResult) => Array.from(mapResult.values()).filter(result => result.isDone).map(result => result.seance))
        )
      : this.svcSeanceRealisation.annulerSeancesSilent(seancesToUpdate);

    const nombreSeances: number = seancesToUpdate.length;
    this.svcLoader.showLoader(this.isToutRealiser ? "Réalisation des " + nombreSeances + " séances..." : "Annulation des " + nombreSeances + " séances");

    seancesUpdated$.pipe(
      switchMap((seances: StoredSeance[]) => this.svcSeance.updateSeances(seances)),
      finalize(() => this.svcLoader.hideLoader())
    ).subscribe();
  }


  private verifierEtatBtnRealiser() {
    this.isToutRealiser = this.seances.some(x => x.status === EStatusSeance.to_be_done);
    this.libelleBtnToutRealiser = this.isToutRealiser ? "Tout réaliser" : "Tout déréaliser";
  }

  public onSeancesListModeSelected(mode: ESeancesListMode): void {
    this.seancesListMode = mode;
  }

  get nbSeancesErreur(): number {
    return Object.keys(this.seanceErreurs).length;
  }

  get messageSeancesErreur(): string {
    const n = this.nbSeancesErreur;
    const pluriel = n > 1;
    return `${n} séance${pluriel ? "s" : ""} ${pluriel ? "n'ont" : "n'a"} pas pu être réalisée${pluriel ? "s" : ""}.`;
  }
}
